import * as React from 'react';
import { Button } from './Button';
import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './Select';
import { cn } from '@ais-ui/utils';
import { PiTimer } from 'react-icons/pi';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
}

export const TimePicker = React.forwardRef<HTMLButtonElement, TimePickerProps>(
  ({ value, onChange, ...props }, ref) => {
    const [time, setTime] = React.useState<string>(value);

    React.useEffect(() => {
      setTime(value);
    }, [value]);

    const handleTimeChange = (type: 'hours' | 'minutes', value: string) => {
      const number = parseInt(value, 10);
      if (isNaN(number)) return; // Exit if the value is not a number

      // Extract current hours and minutes from the value
      let [hours, minutes] = time.split(':').map((v) => parseInt(v, 10));

      // Update hours or minutes based on the type
      if (type === 'hours') {
        hours = number;
      } else {
        minutes = number;
      }

      // Pad single digit hours and minutes with a leading zero
      const updatedHours = hours.toString().padStart(2, '0');
      const updatedMinutes = minutes.toString().padStart(2, '0');

      // Construct the updated time string
      const updatedTime = `${updatedHours}:${updatedMinutes}`;
      if (onChange) {
        onChange(updatedTime);
      }
    };

    return (
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            variant={'outline'}
            className={cn('w-full justify-start text-left font-normal')}
          >
            <PiTimer className="mr-2 size-4" />
            {value}
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start">
          <div className="flex flex-grow p-0 space-x-4">
            <Select
              value={value}
              onValueChange={(value) => handleTimeChange('hours', value)}
            >
              <SelectTrigger>
                <SelectValue>{time.split(':')[0]}</SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-48 overflow-y-auto">
                <SelectGroup>
                  <SelectLabel>Hours</SelectLabel>
                  {Array.from({ length: 24 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>

            <Select
              value={value}
              onValueChange={(value) => handleTimeChange('minutes', value)}
            >
              <SelectTrigger>
                <SelectValue>{time.split(':')[1]}</SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-48 overflow-y-auto w-24">
                <SelectGroup>
                  <SelectLabel>Minutes</SelectLabel>
                  {Array.from({ length: 60 }, (_, i) => (
                    <SelectItem key={i} value={i.toString()}>
                      {i.toString().padStart(2, '0')}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>
        </PopoverContent>
      </Popover>
    );
  },
);
